import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { database } from "../..";
import { handleAuthError, isAuthError } from "../../../utils/handleAuthError";

export interface Promocion {
  id: string;
  titulo: string;
  descripcion: string;
  estado: "activo" | "inactivo";
  color: {
    color1: string;
    color2: string;
  };
  colorTexto: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Función para generar un degradado a partir de un color base
export function generateGradientFromColor(color: string | { color1?: string; color2?: string } | undefined): { color1: string; color2: string } {
  // Si es un objeto (formato antiguo), usar color2 o color1
  if (typeof color === 'object' && color !== null) {
    const oldColor = (color as { color1?: string; color2?: string }).color2 || (color as { color1?: string; color2?: string }).color1 || "#0066CC";
    return generateGradientFromColor(oldColor);
  }

  // Si no es un string válido, usar azul por defecto
  if (!color || typeof color !== 'string') {
    color = "#0066CC";
  }

  // Asegurar que tenga el formato #RRGGBB
  let hex = color.replace('#', '');
  if (hex.length === 3) {
    // Convertir formato corto a largo (#RGB -> #RRGGBB)
    hex = hex.split('').map(char => char + char).join('');
  }
  if (hex.length !== 6) {
    color = "#0066CC";
    hex = "0066CC";
  }

  // Convertir hex a RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Crear versión más clara (aumentar brillo en ~40%)
  const lighten = (value: number) => Math.min(255, Math.round(value + (255 - value) * 0.4));
  const rLight = lighten(r);
  const gLight = lighten(g);
  const bLight = lighten(b);

  // Convertir de vuelta a hex
  const toHex = (value: number) => {
    const hex = value.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return {
    color1: `#${toHex(rLight)}${toHex(gLight)}${toHex(bLight)}`,
    color2: `#${hex}`,
  };
}

export async function getAllPromociones(): Promise<Promocion[]> {
  try {
    const promocionesRef = collection(database, "promociones");
    const q = query(promocionesRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Promocion[];
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error obteniendo promociones:", error);
    throw error;
  }
}

export async function getPromocionById(id: string): Promise<Promocion | null> {
  try {
    const docRef = doc(database, "promociones", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Promocion;
    }
    return null;
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error obteniendo promoción:", error);
    throw error;
  }
}

export async function createPromocion(
  promocion: Omit<Promocion, "id" | "createdAt" | "updatedAt">
): Promise<Promocion> {
  try {
    const promocionesRef = collection(database, "promociones");
    const now = Timestamp.now();

    const newPromocion: Omit<Promocion, "id"> = {
      ...promocion,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = doc(promocionesRef);
    await setDoc(docRef, newPromocion);

    return { id: docRef.id, ...newPromocion };
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error creando promoción:", error);
    throw error;
  }
}

export async function updatePromocion(
  id: string,
  promocion: Partial<Omit<Promocion, "id" | "createdAt" | "updatedAt">>
): Promise<Promocion> {
  try {
    const docRef = doc(database, "promociones", id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Promoción no encontrada");
    }

    const updatedData = {
      ...promocion,
      updatedAt: Timestamp.now(),
    };

    await setDoc(docRef, updatedData, { merge: true });

    const updatedDoc = await getDoc(docRef);
    return { id: updatedDoc.id, ...updatedDoc.data() } as Promocion;
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error actualizando promoción:", error);
    throw error;
  }
}

export async function deletePromocion(id: string): Promise<void> {
  try {
    const docRef = doc(database, "promociones", id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Promoción no encontrada");
    }

    await deleteDoc(docRef);
  } catch (error) {
    if (isAuthError(error)) {
      handleAuthError();
      throw error;
    }
    console.error("Error eliminando promoción:", error);
    throw error;
  }
}
